import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram, Youtube, Twitter, ArrowRight } from "lucide-react";
import { useSettings } from "@/lib/settings";
import { LOGO_URL, COMPANY_NAME, COMPANY_LEGAL_NAME, BRAND_SLOGAN, REG_OFFICE } from "@/lib/brand";

export default function Footer() {
  const { settings } = useSettings();
  if (!settings) return null;

  const logoSrc = settings.logo_url || LOGO_URL;

  const Social = ({ href, Icon, label }) =>
    href ? (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={label}
        className="w-11 h-11 bg-[#1A1A1A] border border-white/10 flex items-center justify-center text-gray-300 hover:bg-[#E65C00] hover:text-white hover:border-[#E65C00] transition-all duration-200"
        data-testid={`footer-social-${label.toLowerCase()}`}
      >
        <Icon className="w-4 h-4" />
      </a>
    ) : null;

  return (
    <footer className="bg-[#0F0F0F] text-gray-300 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      {/* Top CTA strip */}
      <div className="relative bg-[#E65C00]">
        <div className="max-w-7xl mx-auto px-6 py-7 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/80 font-bold">BECOME AN ASSOCIATE</div>
            <div className="font-heading font-black text-xl sm:text-2xl text-white mt-1 uppercase">Earn while you do your current job.</div>
          </div>
          <Link to="/contact?type=associate" className="bg-[#1A1A1A] text-white font-heading font-black uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-black inline-flex items-center gap-2" data-testid="footer-associate-cta">
            Leave a Message <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-12 gap-10">
        {/* Brand block */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <img src={logoSrc} alt={`${COMPANY_NAME} logo`} className="h-16 w-16 object-contain" data-testid="footer-logo" />
            <div className="leading-none">
              <div className="font-heading font-black text-white text-2xl">{COMPANY_NAME.toUpperCase()}</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mt-1.5 font-semibold">{BRAND_SLOGAN}</div>
            </div>
          </div>

          <p className="mt-6 text-sm leading-relaxed text-gray-400 max-w-sm" data-testid="footer-about-text">
            {settings.footer_about_text || "Construction, Mining & Industrial Equipment Trader. A one stop solution for used & new equipment — Buy, Sell & Rent. Authorised dealer for Bull Machines."}
          </p>

          <div className="mt-7">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FFCC00] mb-3">Follow Us</div>
            <div className="flex gap-2 flex-wrap">
              <Social href={settings.facebook || "https://www.facebook.com/"} Icon={Facebook} label="Facebook" />
              <Social href={settings.youtube || "https://www.youtube.com/"} Icon={Youtube} label="YouTube" />
              <Social href={settings.linkedin || "https://www.linkedin.com/company/tara-autohub"} Icon={Linkedin} label="LinkedIn" />
              <Social href={settings.instagram || "https://www.instagram.com/"} Icon={Instagram} label="Instagram" />
              <Social href={settings.twitter || "https://twitter.com/"} Icon={Twitter} label="Twitter" />
            </div>
          </div>
        </div>

        {/* Explore */}
        <div className="md:col-span-2">
          <div className="font-heading font-black uppercase tracking-[0.2em] text-white text-xs mb-5 pb-3 border-b border-[#E65C00]/40">Explore</div>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/about" className="hover:text-[#FFCC00]">About</Link></li>
            <li><Link to="/products" className="hover:text-[#FFCC00]">Products</Link></li>
            <li><Link to="/services" className="hover:text-[#FFCC00]">Services</Link></li>
            <li><Link to="/gallery" className="hover:text-[#FFCC00]">Gallery</Link></li>
          </ul>
        </div>

        {/* Media */}
        <div className="md:col-span-2">
          <div className="font-heading font-black uppercase tracking-[0.2em] text-white text-xs mb-5 pb-3 border-b border-[#E65C00]/40">Media</div>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/blog" className="hover:text-[#FFCC00]">Blog</Link></li>
            <li><Link to="/podcasts" className="hover:text-[#FFCC00]">Podcasts</Link></li>
            <li><Link to="/events" className="hover:text-[#FFCC00]">Events</Link></li>
            <li><Link to="/contact" className="hover:text-[#FFCC00]">Contact</Link></li>
          </ul>
        </div>

        {/* India / Address */}
        <div className="md:col-span-4">
          <div className="font-heading font-black uppercase tracking-[0.2em] text-white text-xs mb-5 pb-3 border-b border-[#E65C00]/40">India</div>
          <div className="font-heading font-black uppercase text-white text-lg leading-tight" data-testid="footer-legal-name">
            {COMPANY_LEGAL_NAME}
          </div>
          <div className="text-sm text-gray-400 mt-4 space-y-2">
            <div>
              <span className="text-[#FFCC00] font-bold">Reg. Office :</span>{" "}
              <span data-testid="footer-reg-office">{REG_OFFICE}</span>
            </div>
            {settings.head_office && (
              <div><span className="text-[#FFCC00] font-bold">{settings.footer_office_label_1 || "Head Office"} :</span> {settings.head_office}</div>
            )}
            {settings.zonal_office && (
              <div><span className="text-[#FFCC00] font-bold">{settings.footer_office_label_2 || "Zonal Office"} :</span> {settings.zonal_office}</div>
            )}
            {(settings.footer_extra_addresses || []).filter((a) => a && (a.address || a.label)).map((a, i) => (
              <div key={i} data-testid={`footer-extra-address-${i}`}>
                <span className="text-[#FFCC00] font-bold">{a.label || "Office"} :</span> {a.address}
              </div>
            ))}
          </div>

          <ul className="space-y-2.5 text-sm mt-5">
            <li className="flex gap-2"><Mail className="w-4 h-4 text-[#FFCC00] mt-0.5 shrink-0" /><a href={`mailto:${settings.email}`} className="hover:text-[#FFCC00]">{settings.email}</a></li>
            <li className="flex gap-2"><Phone className="w-4 h-4 text-[#FFCC00] mt-0.5 shrink-0" /><a href={`tel:${settings.phone}`} className="hover:text-[#FFCC00]">{settings.phone}</a></li>
          </ul>

          <div className="mt-5 text-xs text-gray-500 space-y-1">
            <div><span className="text-gray-400 font-bold">CIN :</span> {settings.cin}</div>
            <div><span className="text-gray-400 font-bold">GST :</span> {settings.gst}</div>
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10 py-5 text-center text-xs text-gray-500">
        {settings.footer_text}
      </div>
    </footer>
  );
}
